import React, { useEffect, useRef, useState } from "react";
import api from "../../core/api/config";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import VincularOrganogramaModal from "./components/VincularOrganogramaModal";
import Select from "react-select";

const MySwal = withReactContent(Swal);

const ConfiguracaoGrupoAcesso = () => {
  const [nomeGrupo, setNomeGrupo] = useState("");
  const [descricao, setDescricao] = useState("");
  const [ativo, setAtivo] = useState(true);
  const [loading, setLoading] = useState(false);

  const [grupos, setGrupos] = useState([]);
  const [selecionados, setSelecionados] = useState([]);
  const [grupoEditando, setGrupoEditando] = useState(null);

  const [cidades, setCidades] = useState([]);
  const [cidadeSelecionada, setCidadeSelecionada] = useState(null);

  const [showModalVinculo, setShowModalVinculo] = useState(false);
  const [grupoParaVinculo, setGrupoParaVinculo] = useState(null);
  const [organogramasGrupo, setOrganogramasGrupo] = useState([]);
  const [vinculosExistentes, setVinculosExistentes] = useState([]);

  const [userId, setUserId] = useState(null);
  const [, setLoadingUser] = useState(true);

  const [busca, setBusca] = useState("");

  useEffect(() => {
    const carregarUsuario = async () => {
      try {
        const { data } = await api.get("/api/user/show");
        setUserId(data.user.id);
      } catch (error) {
        Swal.fire("Erro", "Erro ao carregar usuário logado", "error");
      } finally {
        setLoadingUser(false);
      }
    };

    carregarUsuario();
  }, []);

  const selectAllRef = useRef(null);

  /* PAGINAÇÃO */
  const [page, setPage] = useState(0);
  const [size] = useState(5);
  const [totalPages, setTotalPages] = useState(0);

  /* LISTAR E BUSCAR GRUPOS */
  const listarGrupos = async () => {
    if (!userId) return;

    try {
      const { data } = await api.get("/api/tracking/grupos-acesso", {
        params: {
          userId,
          page,
          size,
          sort: "id,desc",
          busca: busca || null,
        },
      });

      setGrupos(data.content || []);
      setTotalPages(data.totalPages || 0);
      setSelecionados([]);
    } catch {
      MySwal.fire("Erro", "Erro ao listar grupos", "error");
    }
  };

  const carregarAcessos = async () => {
    if (!userId) return;

    try {
      const { data } = await api.get("/api/tracking/usuario/acesso", {
        params: { userId },
      });

      setOrganogramasGrupo(data || []);
    } catch {
      MySwal.fire("Erro", "Erro ao carregar organogramas", "error");
    }
  };

  useEffect(() => {
    if (!userId) return;
    listarGrupos();
  }, [page, userId]);

  useEffect(() => {
    if (!userId) return;
    carregarAcessos();
  }, [userId]);

  /* SELECT ALL */
  useEffect(() => {
    if (!selectAllRef.current) return;
    selectAllRef.current.indeterminate =
      selecionados.length > 0 && selecionados.length < grupos.length;
  }, [selecionados, grupos]);

  const toggleSelecionado = (id) => {
    setSelecionados((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
  };

  const selecionarTodos = () => {
    setSelecionados(
      selecionados.length === grupos.length ? [] : grupos.map((g) => g.id),
    );
  };

  const irPagina = (novaPagina) => {
    if (novaPagina >= 0 && novaPagina < totalPages) setPage(novaPagina);
  };

  /* EDITAR */
  const editarGrupo = (grupo) => {
    setGrupoEditando(grupo);
    setNomeGrupo(grupo.nome);
    setDescricao(grupo.descricao);
    setAtivo(grupo.ativo);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelarEdicao = () => {
    setGrupoEditando(null);
    setNomeGrupo("");
    setDescricao("");
    setAtivo(true);
    setCidadeSelecionada(null);
  };

  /* SALVAR */
  const handleSalvar = async () => {
    if (!nomeGrupo.trim()) {
      return MySwal.fire("Atenção", "Nome do grupo é obrigatório", "warning");
    }

    try {
      setLoading(true);

      const payload = {
        nome: nomeGrupo,
        descricao,
        ativo,
        cidade: cidadeSelecionada?.value || null,
      };

      if (grupoEditando) {
        await api.put(
          `/api/tracking/grupos-acesso/${grupoEditando.id}`,
          payload,
        );

        MySwal.fire("Atualizado", "Grupo atualizado com sucesso", "success");
      } else {
        await api.post("/api/tracking/grupos-acesso", payload);

        MySwal.fire("Criado", "Grupo criado com sucesso", "success");
      }

      cancelarEdicao();
      listarGrupos();
    } catch (error) {
      const mensagemBackend =
        error?.response?.data?.mensagem || error?.response?.data?.message;

      if (error?.response?.status === 400 && mensagemBackend) {
        MySwal.fire("Erro", mensagemBackend, "warning");
      } else {
        MySwal.fire("Erro", "Erro inesperado ao salvar grupo", "error");
      }
    } finally {
      setLoading(false);
    }
  };

  /* ================= REMOVER SELECIONADOS ================= */
  const removerSelecionados = async () => {
    if (selecionados.length === 0) {
      return MySwal.fire("Atenção", "Selecione ao menos um grupo", "warning");
    }

    const confirm = await MySwal.fire({
      title: "Confirmação",
      text: "Deseja remover os grupos selecionados?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Sim, remover",
      cancelButtonText: "Cancelar",
    });

    if (!confirm.isConfirmed) return;

    try {
      setLoading(true);

      await Promise.all(
        selecionados.map((id) =>
          api.delete(`/api/tracking/grupos-acesso/${id}`),
        ),
      );

      MySwal.fire("Removido", "Grupos removidos com sucesso", "success");

      setSelecionados([]);
      listarGrupos(); // recarrega a página atual
    } catch {
      MySwal.fire("Erro", "Erro ao remover grupos", "error");
    } finally {
      setLoading(false);
    }
  };

  /* VINCULAR ORGANOGRAMA */
  const abrirVinculoOrganograma = async (grupoId) => {
    try {
      await carregarAcessos();

      const { data } = await api.get(
        `/api/tracking/grupo-organograma/grupo/${grupoId}`,
        { params: { page: 0, size: 5, sort: "id" } },
      );

      setGrupoParaVinculo(grupoId);
      setVinculosExistentes(data.content || []);
      setShowModalVinculo(true);
    } catch {
      MySwal.fire("Erro", "Erro ao carregar dados", "error");
    }
  };

  /* ================= CIDADES ================= */
  useEffect(() => {
    const fetchCidades = async () => {
      try {
        const { data } = await api.get("/api/tracking/cidade/sp");

        const options = (data || []).map((cidade) => ({
          value: cidade.id,
          label: cidade.nome,
        }));

        setCidades(options);
      } catch {
        MySwal.fire("Erro", "Erro ao carregar cidades", "error");
      }
    };

    fetchCidades();
  }, []);

  useEffect(() => {
    if (!grupoEditando || cidades.length === 0) return;

    const cidadeEncontrada = cidades.find(
      (c) => c.label.toLowerCase() === grupoEditando.cidade?.toLowerCase(),
    );

    if (cidadeEncontrada) {
      setCidadeSelecionada(cidadeEncontrada);
    }
  }, [grupoEditando, cidades]);

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <div className="page-header">
          <div className="page-title">
            <h4>
              {grupoEditando
                ? "Editar Grupo de Acesso"
                : "Configuração do Grupo de Acesso"}
            </h4>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="row">
              {/* FORM */}
              <div className="col-md-6">
                <div className="form-group mb-3">
                  <label className="form-label">Nome do Grupo</label>
                  <input
                    className="form-control"
                    value={nomeGrupo}
                    onChange={(e) => setNomeGrupo(e.target.value)}
                  />
                </div>

                <div className="form-group mb-3">
                  <label className="form-label">Descrição</label>
                  <textarea
                    className="form-control"
                    rows={4}
                    value={descricao}
                    onChange={(e) => setDescricao(e.target.value)}
                  />
                </div>

                <div className="form-group mb-3">
                  <label className="form-label">Cidade *</label>
                  <Select
                    options={cidades}
                    value={cidadeSelecionada}
                    onChange={setCidadeSelecionada}
                    placeholder="Selecione uma opção..."
                  />
                </div>

                <div className="form-group mb-3">
                  <label className="form-label">Status do Grupo</label>

                  <div className="form-check form-switch mt-2">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={!!ativo}
                      onChange={() => setAtivo((prev) => !prev)}
                    />
                    <label className="form-check-label ms-2">
                      {ativo ? "Ativo" : "Inativo"}
                    </label>
                  </div>
                </div>

                {grupoEditando && (
                  <button
                    className="btn btn-outline-secondary"
                    onClick={cancelarEdicao}
                  >
                    Cancelar edição
                  </button>
                )}
              </div>

              {/* LISTA */}
              <div className="col-md-6 border-start">
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <div>
                      <h5 className="mb-0">Grupos de Acesso</h5>
                      <p className="text-muted mb-0">
                        Use o botão <strong>Editar</strong> para alterar um
                        grupo de acesso
                      </p>
                    </div>

                    <button
                      className="btn btn-primary"
                      disabled={selecionados.length !== 1}
                      onClick={() => abrirVinculoOrganograma(selecionados[0])}
                    >
                      <i className="feather-link me-2"></i>
                      Vincular Organograma
                    </button>
                  </div>

                  {/* BUSCA */}
                  <div className="input-group mt-2 gap-2">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Buscar grupo ou cidade..."
                      value={busca}
                      onChange={(e) => setBusca(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          setPage(0);
                          listarGrupos();
                        }
                      }}
                    />

                    <div className="input-group-append">
                      <button
                        className="btn btn-primary"
                        onClick={() => {
                          setPage(0);
                          listarGrupos();
                        }}
                      >
                        <i className="feather-search me-2"></i>
                        Buscar
                      </button>
                    </div>
                  </div>
                </div>

                <div className="table-responsive">
                  <table className="table table-sm table-bordered table-hover align-middle">
                    <thead className="thead-light">
                      <tr>
                        <th width="40">
                          <input
                            type="checkbox"
                            ref={selectAllRef}
                            checked={
                              grupos.length > 0 &&
                              selecionados.length === grupos.length
                            }
                            onChange={selecionarTodos}
                          />
                        </th>
                        <th>Nome</th>
                        <th>Cidade</th>
                        <th width="40"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {grupos.map((grupo) => (
                        <tr
                          key={grupo.id}
                          className={
                            grupoEditando?.id === grupo.id
                              ? "linha-editando"
                              : ""
                          }
                          style={{ cursor: "pointer" }}
                          onClick={() => editarGrupo(grupo)}
                        >
                          <td onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={selecionados.includes(grupo.id)}
                              onChange={() => toggleSelecionado(grupo.id)}
                            />
                          </td>
                          <td>{grupo.nome}</td>
                          <td>{grupo.cidade}</td>
                          <td>
                            <button className="btn btn-outline-light btn-sm">
                              <i className="fas fa-edit text-muted" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* PAGINAÇÃO */}
                <div className="d-flex justify-content-between mt-2">
                  <button
                    className="btn btn-outline-secondary"
                    disabled={page === 0}
                    onClick={() => irPagina(page - 1)}
                  >
                    ← Anterior
                  </button>
                  <span>
                    Página {page + 1} de {totalPages}
                  </span>
                  <button
                    className="btn btn-outline-secondary"
                    disabled={page + 1 >= totalPages}
                    onClick={() => irPagina(page + 1)}
                  >
                    Próxima →
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="d-flex justify-content-between mt-4">
          <button
            className="btn btn-danger"
            disabled={selecionados.length === 0 || loading}
            onClick={removerSelecionados}
          >
            <i className="fas fa-trash me-2" />
            Remover Selecionados
          </button>

          <button
            className="btn btn-primary px-5"
            onClick={handleSalvar}
            disabled={loading}
          >
            <i className="feather-save me-2"></i>
            {loading ? "Salvando..." : "Salvar"}
          </button>
        </div>
      </div>

      <VincularOrganogramaModal
        show={showModalVinculo}
        onClose={() => setShowModalVinculo(false)}
        grupoId={grupoParaVinculo}
        dados={organogramasGrupo}
        vinculosExistentes={vinculosExistentes}
      />
    </div>
  );
};

export default ConfiguracaoGrupoAcesso;
